<template>
  <div class="uk-container uk-text-small">
    <div class="uk-flex">
      <a class="uk-button uk-button-danger print-button uk-align-right" href="javascript:void(0)" @click="close">Close</a>
      <a
        class="uk-button uk-button-primary print-button uk-align-right"
        :class="{ 'uk-disabled': !iframeSrc || isLoading }"
        href="javascript:void(0)"
        @click="printPdf"
      >Print</a>
    </div>

    <div v-if="isLoading" class="uk-margin-top uk-padding-small">
      Loading print preview...
    </div>

    <div
      v-else-if="errorMessage"
      class="uk-margin-top uk-alert-danger uk-border-rounded uk-padding-small"
      role="alert"
    >
      <strong>Unable to load print preview.</strong>
      <div class="uk-margin-small-top">{{ errorMessage }}</div>
    </div>

    <div v-else class="uk-margin-top tw-text-left">
      <div class="tw-text-left">
        <Button :isGreen="true" icon="rotate-right" @click="loadPdf" v-tippy="{content: 'Re-generate PDF', placement: 'bottom'}"></Button>
      
        <div class="tw-text-left tw-inline-block tw-w-[200px] tw-mb-1 tw-mr-1 " v-for="(field, fieldIdx) in userPromptFields">
          <DatePicker v-if="field.field_key.toLowerCase().includes('date')"
            :autofocus="fieldIdx === 0"   
            class="tw-inline-block"
            :textLabel="field.label"
            :input-class="'tw-rounded-lg tw-border tw-border-slate-900 tw-bg-white tw-px-2 tw-py-1.5 tw-text-sm focus:tw-outline-none focus:tw-ring-4 focus:tw-ring-indigo-500/15 focus:tw-border-indigo-500/55'"
            format="MM/DD/YYYY"
            v-model="field.value"
            value-type="format"
            :show-days-shortcuts="true"
            controls-placement="popup"
            @change="loadPdf"
          ></DatePicker>
          <label v-else>
            {{ field.label }}
            <input
              type="text"
              class="tw-mt-1 tw-w-full tw-rounded-lg tw-border tw-border-slate-900/70 tw-bg-white tw-px-3 tw-py-2 tw-text-sm tw-text-slate-900 tw-shadow-sm tw-shadow-slate-900/5 placeholder:tw-text-slate-400 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-indigo-500/40"
              v-model="field.value"
              @blur="loadPdf"
            />
          </label>
        </div>
      </div>
      <div class="tw-bg-amber-300" v-if="printedDate">Print started {{  printedDateFromNowString}} (at {{ printedDate?.toLocaleTimeString() }})</div>
      <iframe
        ref="pdfFrame"
        :src="iframeSrc"
        style="width: 100%; height: calc(100vh - 120px); border: 1px solid #e5e7eb; border-radius: 8px;"
      ></iframe>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref } from 'vue';
import DatePicker from '@/components/core/DateTime/DatePicker.vue';
import Button from '@/components/core/Button.vue';
import GetCompanyTemplatesQuery from '@/gql/document_builder/GetCompanyTemplates.gql';
import GetTemplateFieldsQuery from '@/gql/document_builder/GetTemplateFields.gql';
import { useAuthStore } from '@/stores/auth';
import moment from 'moment';
import { useIdleDetection } from '@/composables/useIdleDetection';

export default defineComponent({
  name: 'PrintBL',
  components: {  DatePicker, Button },
  setup() {
    // update last printed date when NOT idle
    const { isIdle, } = useIdleDetection({ timeout: 30_000 });
    return { isIdle };
  },
  data() {
    return {
      id: (this as any).$route.params.id,
      containerIdx: (this as any).$route.params.containerIdx,
      iframeSrc: '' as string,
      isLoading: true,
      errorMessage: '' as string,
      userPromptFields: [],
      printedDate: null,
      refreshPrintedDateFromNowString: 0
    };
  },
  computed: {
    pdfUrl(): string {
      return `/api/document-builder/generate-bl/order/${(this as any).id}/container/${(this as any).containerIdx}`;
    },
    printedDateFromNowString: function() {
      const vm = this as any;
      if (vm.refreshPrintedDateFromNowString) {
        return moment(vm.printedDate).fromNow()
      }
      return 'now'
    }
  },
  async mounted() {
    const vm = this as any;
    try {
      await vm.fetchPromptFields();
      await vm.loadPdf();
    } catch (error: any) {
      vm.errorMessage = vm.toFriendlyErrorMessage(error);
    } finally {
      vm.isLoading = false;
    }
  },
  beforeUnmount() {
    const vm = this as any;
    if (vm.iframeSrc && String(vm.iframeSrc).startsWith('blob:')) {
      URL.revokeObjectURL(vm.iframeSrc);
    }
  },
  watch: {
    isIdle(idle: boolean) {
      const vm = this as any;
      if (idle) {
        vm.refreshPrintedDateFromNowString++;
      }
    }
  },
  methods: {
    toFriendlyErrorMessage(err: unknown): string {
      const message = String((err as any)?.message || 'Unknown error');
      const code = String((err as any)?.code || '').toUpperCase();

      if (code === 'BL_TEMPLATE_MISSING') {
        return 'Template missing. Please set a default BL template in Document Template Editor and try again.';
      }

      if (code === 'AUTHENTICATION_REQUIRED') {
        return 'Your session expired. Please sign in again and retry.';
      }

      if (code === 'ACCESS_DENIED') {
        return 'You do not have access to this order/container.';
      }

      if (code === 'ORDER_NOT_FOUND' || code === 'CONTAINER_NOT_FOUND') {
        return 'The requested print data was not found.';
      }

      if (code === 'CONTAINER_NOT_ASSOCIATED_WITH_ORDER') {
        return 'Container is not associated with this order.';
      }

      return message;
    },
    async parseErrorResponse(res: Response): Promise<{ code?: string; message: string }> {
      const contentType = (res.headers.get('content-type') || '').toLowerCase();

      if (contentType.includes('application/json')) {
        const data = await res.json().catch(() => ({} as any));
        const errorNode = (data as any)?.error || {};
        return {
          code: errorNode?.code,
          message: String(errorNode?.message || `Failed to load BL PDF (${res.status})`),
        };
      }

      const text = await res.text().catch(() => '');
      return { message: text || `Failed to load BL PDF (${res.status})` };
    },
    async fetchPromptFields() {
      const vm = this as any;
      const templatesResp = await vm.$apollo.query({
        query: GetCompanyTemplatesQuery,
        fetchPolicy: 'network-only',
      });
      const templates = templatesResp?.data?.companyDocumentTemplates || [];
      const defaultTemplate = templates.find((t: any) => t.modelType === 'BL' && t.isDefault);
      if (!defaultTemplate?.id) return [];

      const fieldsResp = await vm.$apollo.query({
        query: GetTemplateFieldsQuery,
        variables: { templateId: Number(defaultTemplate.id) },
        fetchPolicy: 'network-only',
      });

      const templateFields = fieldsResp?.data?.templateFields || [];
      vm.userPromptFields = templateFields
        .filter((f: any) => {
          const type = f?.fieldType || (f?.fieldKey === 'static.text' ? 'static' : 'dynamic');
          return type === 'user_prompt';
        })
        .map((f: any) => ({
          field_key: f.fieldKey,
          label: f.promptLabel || (String(f.fieldKey || '').startsWith('custom.') ? String(f.fieldKey).slice('custom.'.length) : f.fieldKey),
        }));
    },
    async loadPdf() {
      const vm = this as any;
      vm.errorMessage = '';
      const token = useAuthStore().token;
      const authHeader = token ? (token.startsWith('Bearer ') ? token : `Bearer ${token}`) : '';
      const promptValues = Object.fromEntries(vm.userPromptFields.map(f => [f.field_key, f.value])) ;
      const url = new URL(vm.pdfUrl, window.location.origin);
      if (Object.keys(promptValues).length > 0) {
        url.searchParams.set('promptValues', JSON.stringify(promptValues));
      }

      const res = await fetch(url.toString(), {
        method: 'GET',
        headers: authHeader ? { Authorization: authHeader } : {},
      });

      if (!res.ok) {
        const parsedError = await vm.parseErrorResponse(res);
        const err = new Error(parsedError.message) as Error & { code?: string };
        err.code = parsedError.code;
        throw err;
      }

      const blob = await res.blob();
      if (vm.iframeSrc && String(vm.iframeSrc).startsWith('blob:')) {
        URL.revokeObjectURL(vm.iframeSrc);
      }
      vm.iframeSrc = URL.createObjectURL(blob);
    },
    close() {
      window.open('', '_self').close();
    },
    printPdf() {
      const vm = this as any;
      
      vm.printedDate = null;
      if (!vm.iframeSrc || vm.isLoading || vm.errorMessage) return;
      const frame = ((this as any).$refs?.pdfFrame as HTMLIFrameElement | undefined);
      
      frame?.contentWindow?.focus();
      frame?.contentWindow?.print();
      setTimeout(() => {
        vm.printedDate = new Date();
      }, 500);     
    }
  },
} as any);
</script>

<style lang="scss" scoped>
table {
  border-collapse: collapse;
}
td {
  border: 1px solid black;
  border-collapse: collapse;
}

tbody,
thead {
  border-collapse: separate;
}
th {
  font-size: x-small;
  border-collapse: separate;
}

.x-small {
  font-size: x-small;
}

td {
  padding: 0px;
}

.uk-text-larger {
  font-size: 1.1rem;
}
.uk-text-justify {
  font-size: small;
}
@media print {
  .uk-text-larger {
    font-size: 0.9rem;
  }

  td.longheight {
    height: 35px;
  }
  #printPageButton {
    display: none;
  }
  .print-button {
    display: none;
  }
  input[type='checkbox'] {
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  input[type='checkbox'][checked] {
    background-color: black !important;
  }
}
</style>
